"use client";
import { useState } from 'react';
import { useSpring, animated, useInView, useSprings } from '@react-spring/web';
import { AlertCircle, CheckCircle, Gift, CreditCard, Shield, Timer } from 'lucide-react';
import RegistrationModal from '../ui/RegistrationModal';
import WaitlistModal from '../ui/WaitlistModal';
import { useSeatsData } from '@/hooks/useSeatsData';

export default function RegistrationSection() {
  const { seatColor, remainingSeats } = useSeatsData();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isWaitlistModalOpen, setIsWaitlistModalOpen] = useState(false);

  const included = [
    "Acreditación certificado/a por Zinquo, ICF y MDS Group",
    "Manual completo del Modelo Triaxial",
    "Juego/herramienta el Valor de los Valores",
    "Acceso a comunidad privada de coaches certificados",
    "Script de taller testado y listo para impartir",
    "Plantillas y recursos listos para usar",
    "Acceso durante 1 año al campus online",
    "Acompañamiento directo por Toño Galán y equipo",
    "Sesiones de resolución de dudas en directo"
  ];

  const bonuses = [
    { value: "300€", item: "Mentoría 1:1 con Toño" },
    { value: "600€", item: "AQUELLOS QUE COMPRARON EN EL EVENTO EVDLV. 12 meses Zinquo Premium" },
    { value: "1.495€", item: "Formación online certificada" },
    { value: "525€", item: "SOLO A LOS 30 PRIMEROS PENSIÓN COMPLETA + ALOJAMIENTO" }
  ];

  // Header animations
  const [bannerRef, bannerInView] = useInView({ once: true, rootMargin: '-80px' });
  const bannerSpring = useSpring({
    opacity: bannerInView ? 1 : 0,
    transform: bannerInView ? 'translateY(0px)' : 'translateY(-20px)',
    config: { duration: 400 }
  });

  const [headerRef, headerInView] = useInView({ once: true, rootMargin: '-80px' });
  const headerSpring = useSpring({
    opacity: headerInView ? 1 : 0,
    config: { duration: 400 }
  });

  // Value stack animation
  const [valueRef, valueInView] = useInView({ once: true, rootMargin: '-80px' });
  const valueSpring = useSpring({
    opacity: valueInView ? 1 : 0,
    transform: valueInView ? 'translateX(0px)' : 'translateX(-20px)',
    config: { duration: 400 }
  });

  // Investment box animation
  const [investmentRef, investmentInView] = useInView({ once: true, rootMargin: '-80px' });
  const investmentSpring = useSpring({
    opacity: investmentInView ? 1 : 0,
    transform: investmentInView ? 'translateX(0px)' : 'translateX(20px)',
    config: { duration: 400 },
    delay: 100
  });

  // Included items animations
  const [includedRef, includedInView] = useInView({ once: true, rootMargin: '-80px' });
  const includedSprings = useSprings(
    included.length,
    included.map((_, index) => ({
      opacity: includedInView ? 1 : 0,
      transform: includedInView ? 'translateX(0px)' : 'translateX(-20px)',
      config: { duration: 400 },
      delay: index * 30
    }))
  );

  // Bonuses animations
  const [bonusesRef, bonusesInView] = useInView({ once: true, rootMargin: '-80px' });
  const bonusesSprings = useSprings(
    bonuses.length,
    bonuses.map((_, index) => ({
      opacity: bonusesInView ? 1 : 0,
      transform: bonusesInView ? 'translateX(0px)' : 'translateX(-20px)',
      config: { duration: 400 },
      delay: index * 50
    }))
  );

  // Inclusions value animation
  const [inclusionsValueRef, inclusionsValueInView] = useInView({ once: true, rootMargin: '-80px' });
  const inclusionsValueSpring = useSpring({
    opacity: inclusionsValueInView ? 1 : 0,
    transform: inclusionsValueInView ? 'translateY(0px)' : 'translateY(10px)',
    config: { duration: 400 }
  });

  // Payment options animations
  const [paymentRef, paymentInView] = useInView({ once: true, rootMargin: '-80px' });
  const paymentOptionsSprings = useSprings(
    3,
    [0, 1, 2].map(index => ({
      opacity: paymentInView ? 1 : 0,
      transform: paymentInView ? 'translateX(0px)' : 'translateX(-20px)',
      config: { duration: 400 },
      delay: index * 50
    }))
  );

  // Guarantee animation
  const [guaranteeRef, guaranteeInView] = useInView({ once: true, rootMargin: '-80px' });
  const guaranteeSpring = useSpring({
    opacity: guaranteeInView ? 1 : 0,
    config: { duration: 400 }
  });

  // Final urgency animation
  const [urgencyRef, urgencyInView] = useInView({ once: true, rootMargin: '-80px' });
  const urgencySpring = useSpring({
    opacity: urgencyInView ? 1 : 0,
    transform: urgencyInView ? 'translateY(0px)' : 'translateY(20px)',
    config: { duration: 400 }
  });

  return (
    <section id="registration" className="pt-16 md:pt-24 bg-white relative overflow-hidden">

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Urgency banner */}
        <animated.div
          ref={bannerRef}
          className="bg-red-500 text-white p-4 rounded-2xl mb-12 text-center"
          style={bannerSpring}
        >
          <div className="flex items-center justify-center gap-3">
            <AlertCircle className="w-6 h-6" />
            <p className="text-lg md:text-xl font-bold">
              ATENCIÓN: Esta es la única edición en el mundo en 2025 realizada en formato retiro
            </p>
          </div>
        </animated.div>

        {/* Main headline */}
        <animated.div
          ref={headerRef}
          className="text-center mb-16"
          style={headerSpring}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading uppercase tracking-tight text-black mb-6">
            Tu decisión más rentable
            <span className="block text-[#97d700]">empieza aquí</span>
          </h2>
          <div className="inline-block text-black px-6 py-3 ">
            <p className="text-xl font-heading uppercase tracking-wider">
              AMAROO, ALICANTE · 18-20 JULIO 2025
            </p>
          </div>
        </animated.div>

        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          {/* Value stack */}
          <animated.div
            ref={valueRef}
            className="bg-white p-8 rounded-3xl shadow-xl"
            style={valueSpring}
          >
            <h3 className="text-2xl font-heading uppercase tracking-wider text-black mb-6">
              Todo lo que te llevas
            </h3>
            <div ref={includedRef} className="space-y-3 mb-8">
              {includedSprings.map((style, index) => (
                <animated.div
                  key={index}
                  style={style}
                  className="flex items-start gap-3"
                >
                  <CheckCircle className="w-5 h-5 text-[#97d700] mt-1 flex-shrink-0" />
                  <p className="text-gray-700">{included[index]}</p>
                </animated.div>
              ))}
            </div>

            {/* Inclusions value */}
            <animated.div
              ref={inclusionsValueRef}
              style={inclusionsValueSpring}
              className="text-center mb-6 p-4 bg-gray-50 rounded-xl"
            >
              <p className="text-lg text-gray-700">
                Valor total de lo incluido: <span className="font-bold text-[#97d700] text-xl">1.495€ <span className="text-sm font-normal">+IVA</span></span>
              </p>
            </animated.div>

            {/* Bonuses */}
            <div className="bg-[#97d700]/10 p-6 rounded-2xl">
              <div className="flex items-center gap-2 mb-4">
                <Gift className="w-5 h-5 text-[#97d700]" />
                <p className="font-bold text-lg">BONUS EXCLUSIVOS</p>
              </div>
              <div ref={bonusesRef} className="space-y-2">
                {bonusesSprings.map((style, index) => (
                  <animated.div
                    key={index}
                    style={style}
                    className="flex justify-between items-center"
                  >
                    <p className="text-sm">{bonuses[index].item}</p>
                    <span className="font-bold text-[#97d700]">{bonuses[index].value}</span>
                  </animated.div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-300">
                <div className="flex justify-between items-center">
                  <p className="font-bold">Valor total de bonuses:</p>
                  <span className="text-2xl font-heading text-[#97d700]">2.920€ <span className="text-sm font-normal">+IVA</span></span>
                </div>
              </div>
            </div>
          </animated.div>

          {/* Investment box */}
          <animated.div
            ref={investmentRef}
            className="bg-black text-white p-8 rounded-3xl"
            style={investmentSpring}
          >
            <div className="text-center mb-8">
              <p className="text-[#97d700] font-bold uppercase tracking-wider mb-4">
                Inversión total
              </p>
              <div className="relative">
                <p className="text-gray-500 text-xs mb-2"><span className="text-2xl line-through">4.415€</span> +IVA</p>
                <p className="text-6xl md:text-7xl font-heading text-white mb-2">
                  1.495€ <span className="text-2xl font-normal">+IVA</span>
                </p>
                <div className="absolute -top-4 -right-4 bg-red-500 text-white px-4 py-2 rounded-full text-sm font-bold transform rotate-12">
                  -66%
                </div>
              </div>
            </div>

            {/* Payment options */}
            <div ref={paymentRef} className="mb-8">
              <div className="flex items-center gap-2 mb-6 justify-center">
                <CreditCard className="w-5 h-5 text-[#97d700]" />
                <p className="font-bold text-white">Elige tu plan de pago</p>
              </div>
              
              <div className="space-y-3">
                {/* One-time payment - Featured */}
                <animated.div
                  style={paymentOptionsSprings[0]}
                  className="relative bg-gradient-to-r from-[#97d700]/20 to-[#97d700]/10 p-4 rounded-2xl border border-[#97d700]/30 hover:border-[#97d700]/50 transition-all duration-300 group"
                >
                  <div className="absolute -top-3 left-6 bg-[#97d700] text-black px-3 py-1 rounded-full text-xs font-bold">
                    MEJOR VALOR
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-bold text-white text-lg">Pago único</p>
                      <p className="text-gray-400 text-sm">Ahorra en tu inversión total</p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-heading text-[#97d700]">1.495€</p>
                      <p className="text-sm text-gray-400">+IVA</p>
                    </div>
                  </div>
                </animated.div>

                {/* 3 months payment */}
                <animated.div
                  style={paymentOptionsSprings[1]}
                  className="bg-white/5 p-4 rounded-2xl border border-white/10 hover:border-white/20 transition-all duration-300 group"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-bold text-white">Reserva + 3 cuotas mensuales</p>
                      <p className="text-gray-400 text-sm">Flexibilidad a corto plazo</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-heading text-white">315€ + 445€<span className="text-lg">/mes</span></p>
                      <p className="text-sm text-gray-400">+IVA</p>
                    </div>
                  </div>
                </animated.div>

                {/* 6 months payment */}
                <animated.div
                  style={paymentOptionsSprings[2]}
                  className="bg-white/5 p-4 rounded-2xl border border-white/10 hover:border-white/20 transition-all duration-300 group"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-bold text-white">Reserva + 6 cuotas mensuales</p>
                      <p className="text-gray-400 text-sm">Máxima comodidad</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-heading text-white">315€ + 247,50€<span className="text-lg">/mes</span></p>
                      <p className="text-sm text-gray-400">+IVA</p>
                    </div>
                  </div>
                </animated.div>
              </div>
            </div>

            {/* Guarantee */}
            <animated.div
              ref={guaranteeRef}
              style={guaranteeSpring}
              className="bg-[#97d700]/20 p-6 rounded-2xl mb-8"
            >
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-5 h-5 text-[#97d700]" />
                <p className="font-bold text-[#97d700]">Garantía de satisfacción</p>
              </div>
              <p className="text-sm">
                Si en las primeras 4 horas sientes que no es para ti,
                te devolvemos el 100% de tu inversión. Sin preguntas.
              </p>
            </animated.div>

            {/* CTA */}
            {remainingSeats === 0 ? (
              <>
                <button
                  onClick={() => setIsWaitlistModalOpen(true)}
                  className="block w-full text-center px-8 py-5 bg-red-500 text-white font-heading uppercase tracking-wider font-bold text-xl rounded-full hover:bg-red-600 transform hover:scale-105 transition-all duration-300"
                  aria-label="Apuntarse a la lista de espera para próximas ediciones"
                >
                  APUNTARME A PRÓXIMAS EDICIONES
                </button>
                <div className="flex items-center justify-center gap-2 text-gray-400 text-sm mt-4">
                  <p>Te avisaremos en cuanto se abran las inscripciones</p>
                </div>
              </>
            ) : (
              <>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="block w-full text-center px-8 py-5 bg-[#97d700] text-black font-heading uppercase tracking-wider font-bold text-xl rounded-full hover:bg-[#b5ff00] transform hover:scale-105 transition-all duration-300"
                  aria-label="Solicitar admisión para la certificación"
                >
                  SOLICITAR MI ADMISIÓN
                </button>
                <div className="flex items-center justify-center gap-2 text-gray-400 text-sm mt-4">
                  <p>Respuesta en menos de 48h</p>
                </div>
              </>
            )}
          </animated.div>
        </div>

        {/* Final urgency */}
        <animated.div
          ref={urgencyRef}
          className="bg-gradient-to-r from-red-500 to-red-600 text-white p-8 rounded-3xl text-center"
          style={urgencySpring}
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Timer className="w-8 h-8" />
            <p className="text-2xl md:text-3xl font-heading uppercase tracking-wider">
              El tiempo corre en tu contra
            </p>
          </div>
          <p className="text-xl mb-6">
            Cada día que pasa es un día menos transformando vidas y cobrando lo que vales
          </p>
          <div className="inline-flex items-center gap-2 bg-white/20 px-6 py-2 rounded-full">
            {remainingSeats === 0 ? (
              <span className="text-xl font-heading">TODAS LAS PLAZAS VENDIDAS</span>
            ) : (
              <>
                <span className="text-3xl font-heading">{remainingSeats}</span>
                <span className="text-lg">plazas restantes</span>
                <span
                  className="w-2 h-2 rounded-full animate-pulse"
                  style={{ backgroundColor: seatColor }}
                ></span>
              </>
            )}
          </div>
          <p className="text-lg mt-4 font-medium">
            {Math.max(0, remainingSeats - 15) === 0 
              ? '' 
              : `Solo ${Math.max(0, remainingSeats - 15)} disponible${Math.max(0, remainingSeats - 15) === 1 ? '' : 's'} con alojamiento incluido`}
          </p>
        </animated.div>
      </div>

      {/* Registration Modal */}
      <RegistrationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />

      {/* Waitlist Modal */}
      <WaitlistModal
        isOpen={isWaitlistModalOpen}
        onClose={() => setIsWaitlistModalOpen(false)}
      />
    </section>
  );
}